import { Component, effect, model, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../core/services/user.service';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { CustomValidators } from '../../core/validators/custom-validators';
import { UpdateUserRequest, UserModel } from '../../core/models/data/user.model';
import { InputLabel } from "../../parts/input-label/input-label.component";
import { PhoneInput } from "../../parts/phone-input/phone-input.component";
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { UserSessionService } from '../../core/services/user-session.service';
import { ServiceResultModel } from '../../core/models/service/service-result.model';

@Component({
  selector: 'ms-update-user-form',
  imports: [InputLabel, PhoneInput, ReactiveFormsModule],
  templateUrl: './update-user.form.html',
})
export class UpdateUserForm extends BaseFormComponent {
  public updateUserForm: FormGroup;
  public submitSuccess: boolean | null = null;
  public cancelRequested = output();
  public editConfirmationRequested = output();
  public submissionResult = output<ServiceResultModel<UserModel>>();
  public selectedUser = model<UserModel | null>();

  constructor(private userService: UserService, private fb: FormBuilder, private userSessionService: UserSessionService) {
    super();
    this.updateUserForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      telephone: [{ countryCode: '1', phoneNumber: '', extension: '' }, [CustomValidators.phone]]
    });

    effect(() => {
      const user = this.selectedUser();
      if (user) {
        this.updateUserForm.patchValue({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.emailAddress || '',
          telephone: {
            countryCode: user.phoneCountryCode || '1',
            phoneNumber: user.phoneNumber || '',
            extension: user.phoneExtension || ''
          }
        });
      }
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public onSubmit(): void {
    if (this.updateUserForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.editConfirmationRequested.emit();
  }

  public async updateUser(): Promise<void> {
    this.isSubmitting = true;

    const updateUser = {
      firstName: this.updateUserForm.value.firstName,
      lastName: this.updateUserForm.value.lastName,
      phoneCountryCode: this.updateUserForm.value.telephone?.countryCode,
      phoneNumber: this.updateUserForm.value.telephone?.phoneNumber,
      phoneExtension: this.updateUserForm.value.telephone?.extension,
      avatarHexColor: this.selectedUser()!.avatarHexColor,
      clearAvatarFileId: false
    } as UpdateUserRequest;

    const sanitizedUser = this.setPhonePropertiesToNullIfEmpty(updateUser);

    try {
      const response = await this.userService.updateUser(sanitizedUser, this.selectedUser()!.userId);

      if (response.success === true) {
        this.resetForm();
        this.submitSuccess = true;

        this.submissionResult.emit(response);
        const activeUser = this.userSessionService.activeUser();

        if (this.selectedUser()?.userId === activeUser?.userId) {
          this.userSessionService.updateActiveUser(response.data!);
        }
      } else {
        this.submitSuccess = false;
        this.submissionResult.emit(response);
      }
    } catch (error) {
      this.submitSuccess = false;
      this.submissionResult.emit(<ServiceResultModel>{
        success: false,
        message: 'Something went wrong while updating the user. Please try again.',
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  public resetForm(): void {
    const user = this.selectedUser();
    if (user) {
      this.updateUserForm.patchValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.emailAddress || '',
        telephone: {
          countryCode: user.phoneCountryCode || '1',
          phoneNumber: user.phoneNumber || '',
          extension: user.phoneExtension || ''
        }
      });
    }
    this.updateUserForm.markAsUntouched();
    this.submitSuccess = null;
  }

  private markAllFieldsAsTouched(): void {
    this.updateUserForm.markAllAsTouched();
  }

  public getFormState(): any {
    return {
      formValue: this.updateUserForm.value,
      formValid: this.updateUserForm.valid,
      formTouched: this.updateUserForm.touched,
      formDirty: this.updateUserForm.dirty,
      telephoneErrors: this.updateUserForm.get('telephone')?.errors
    };
  }

  public isAnyFieldChanged(): boolean {
    return this.updateUserForm.value.firstName !== (this.selectedUser()?.firstName || '') ||
           this.updateUserForm.value.lastName !== (this.selectedUser()?.lastName || '') ||
           this.updateUserForm.value.telephone?.countryCode !== (this.selectedUser()?.phoneCountryCode || '1') ||
           this.updateUserForm.value.telephone?.phoneNumber !== (this.selectedUser()?.phoneNumber || '') ||
           this.updateUserForm.value.telephone?.extension !== (this.selectedUser()?.phoneExtension || '');
  }

  private setPhonePropertiesToNullIfEmpty(user: UpdateUserRequest): UpdateUserRequest {
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneCountryCode)) {
      user.phoneCountryCode = null;
    }
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneNumber)) {
      user.phoneCountryCode = null;
      user.phoneNumber = null;
    }
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneExtension)) {
      user.phoneExtension = null;
    }

    return user;
  }
}
